// Copyright 2017 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { RecordingGroupContainer, RecordingGroupMetadata } from "datatypes/Containers";
import {
    IRecordingGroupContainerStorageService,
    KnownMetadataPropertyName,
    RecordingTransformer,
} from "services/Storage/IRecordingGroupContainerStorageService";
import { IStorageService, StorageTypes } from "services/Storage/IStorageService";
import { StorageServiceExtensions } from "services/Storage/StorageServiceExtensions";

interface KnownRecordingGroupMetadataProperty {
    key: KnownMetadataPropertyName;
    recordingTransformer?: RecordingTransformer;
    defaultValue?: unknown;
    isReadOnly?: boolean;
}

const defaultRecordingTransformer = (key: string, value: unknown) => {
    return { [key]: value };
};

const nameRecordingTransformer = (
    key: string,
    value: unknown,
    id: string,
    idx: number,
    meta: RecordingGroupMetadata
) => {
    const stepWithRecording = meta.steps.find((x) => x.recordingId === id);
    const postFix = stepWithRecording ? " " + (stepWithRecording.stepIndex + 1) + "/" + meta.steps.length : "";
    return { name: meta.name + postFix };
};

@autoinject
export class RecordingGroupContainerStorageService implements IRecordingGroupContainerStorageService {
    //#region Known RecordingGroup metadata properties
    private knownMetadataProperties: KnownRecordingGroupMetadataProperty[] = [
        // Properties that propagate trough to Recordings
        { key: "name", recordingTransformer: nameRecordingTransformer },
        { key: "notes", recordingTransformer: defaultRecordingTransformer },
        { key: "distance", recordingTransformer: defaultRecordingTransformer },
        { key: "metadata", recordingTransformer: defaultRecordingTransformer, defaultValue: [] },

        // Recording group only properties
        { key: "steps", recordingTransformer: nameRecordingTransformer, defaultValue: [] },
        { key: "recordings", defaultValue: [], isReadOnly: true },
        { key: "recommendedMeasurementKindName" },

        // Silverlight export properties
        { key: "projectIds", defaultValue: [] },
        { key: "measurementTypes", defaultValue: [] },
        { key: "measurementIds", defaultValue: [] },
    ];
    //#endregion

    //#region Injected Services
    private storageService: IStorageService;
    //#endregion

    //#region Constructor
    constructor(storageService: IStorageService) {
        this.storageService = storageService;
    }
    //#endregion

    //#region
    public create(): Promise<RecordingGroupContainer> {
        let createdContainerId: string;
        return this.storageService
            .create(StorageTypes.recordingGroup)
            .then((containerId) => {
                createdContainerId = containerId;

                // Create dictionary like type for all properties to set
                const props: { [key: string]: unknown } = {};

                for (const kvp of this.knownMetadataProperties) {
                    props[kvp.key] = kvp.defaultValue;
                }

                // Initialise initial known properties
                return this.storageService
                    .updateMetadataSet(containerId, props)
                    .then(() => this.getRecordingGroupContainerFromId(containerId));
            })
            .catch((e: Error) => {
                // Delete the container if something went wrong while updating initial metadata and ignore any errors possibly thrown when deleting.
                this.storageService.delete(createdContainerId).catch(() => {});

                // Re-throw error for catching further down the line
                throw e;
            });
    }

    public load(id: string): Promise<RecordingGroupContainer> {
        return this.getRecordingGroupContainerFromId(id);
    }

    public delete(id: string): Promise<void> {
        return this.storageService.load(id).then((container: RecordingGroupContainer) => {
            // Get metadata
            const recordingGroupMetadata = container.metadata;

            if (!recordingGroupMetadata || !recordingGroupMetadata.recordings) {
                return this.storageService.delete(id);
            }

            // Delete all recordings
            const deleteRecordingPromises = recordingGroupMetadata.recordings.map((x) => this.storageService.delete(x));

            // Delete recording group
            const deleteRecordingGroupPromise = this.storageService.delete(id);

            return Promise.all(deleteRecordingPromises.concat(deleteRecordingGroupPromise)).then(() => {});
        });
    }

    public updateMetadata(
        id: string,
        key: string,
        value: unknown,
        recordingTransformer?: RecordingTransformer
    ): Promise<void> {
        if (!id) {
            // Nothing to do..
            return Promise.resolve();
        }

        const updateMeasurementPromise = this.storageService.updateMetadata(id, key, value);
        let updateRecordingsPromise = Promise.resolve();

        // Also save data to recordings if there is a transformer available
        if (recordingTransformer) {
            // Get recording id's from metadata of the recording group on the disk
            updateRecordingsPromise = this.storageService.getMetadata<RecordingGroupMetadata>(id).then((metadata) => {
                const recordings = metadata.recordings;
                const updateRecordingsPromises = recordings.map((recordingId, recordingIdx, recordings) => {
                    // Call the recordingTransformer for this key
                    const transformedProps = recordingTransformer(key, value, recordingId, recordingIdx, metadata);

                    // Update properties based on the results of the recordingTransformer (which can return multiple keys that have been transformed)
                    for (const propKey in transformedProps) {
                        if (Object.prototype.hasOwnProperty.call(transformedProps, propKey))
                            this.storageService.updateMetadata(recordingId, propKey, transformedProps[propKey]);
                    }
                });
                return Promise.all(updateRecordingsPromises).then(() => {});
            });
        }

        return updateMeasurementPromise.then(() => updateRecordingsPromise);
    }

    public updateMetadataSet(
        id: string,
        kvps: { [key: string]: unknown },
        recordingTransformers?: { [key: string]: RecordingTransformer }
    ): Promise<void> {
        const updateMeasurementPromise = this.storageService.updateMetadataSet(id, kvps);
        let updateRecordingsPromise = Promise.resolve();

        // Also save data to recordings if there is a transformer available
        if (recordingTransformers) {
            // Get recording id's from metadata of the recording group on the disk
            updateRecordingsPromise = this.storageService.getMetadata<RecordingGroupMetadata>(id).then((metadata) => {
                const recordings = metadata.recordings;
                const updateRecordingsPromises = recordings.map((recordingId, recordingIdx, recordings) => {
                    const transformedKvps: { [key: string]: RecordingTransformer } = {};

                    // Apply transformer for each key present in kvps
                    for (const key in kvps) {
                        // Check if the recordingsTransformers dictionary has a key matching the kvps, if not, dont update this property for the recording
                        if (
                            Object.prototype.hasOwnProperty.call(kvps, key) &&
                            Object.prototype.hasOwnProperty.call(recordingTransformers, key) &&
                            recordingTransformers[key]
                        ) {
                            // Call the recordingTransformer for this key
                            const transformedProps = recordingTransformers[key](
                                key,
                                kvps[key],
                                recordingId,
                                recordingIdx,
                                metadata
                            ) as { [key: string]: RecordingTransformer };

                            // Update properties based on the results of the recordingTransformer (which in turn can return multiple keys that have been transformed)
                            for (const propKey in transformedProps) {
                                if (Object.prototype.hasOwnProperty.call(transformedProps, propKey))
                                    transformedKvps[propKey] = transformedProps[propKey];
                            }
                        }
                    }

                    return this.storageService.updateMetadataSet(recordingId, transformedKvps);
                });
                return Promise.all(updateRecordingsPromises).then(() => {});
            });
        }

        return updateMeasurementPromise.then(() => updateRecordingsPromise);
    }

    public updateKnownMetadata(id: string, key: KnownMetadataPropertyName, value: unknown): Promise<void> {
        const knownMetadataProperty = this.knownMetadataProperties.find((x) => x.key === key);

        if (!knownMetadataProperty) {
            return Promise.reject(new Error(key + " is not a known metadata property."));
        }

        if (knownMetadataProperty.isReadOnly) {
            return Promise.reject(new Error(key + " is a read-only property and can not be updated via this method."));
        }

        return this.updateMetadata(id, key, value, knownMetadataProperty.recordingTransformer);
    }

    public updateKnownMetadataSet(id: string, kvps: { [key: string]: RecordingTransformer }): Promise<void> {
        // Validate keys and map recordingTransformers
        const recordingTransformers: { [key: string]: RecordingTransformer } = {};
        for (const key in kvps) {
            if (Object.prototype.hasOwnProperty.call(kvps, key)) {
                const knownMetadataProperty = this.knownMetadataProperties.find((x) => x.key === key);

                if (!knownMetadataProperty) {
                    return Promise.reject(new Error(key + " is not a known metadata property."));
                }

                if (knownMetadataProperty.isReadOnly) {
                    return Promise.reject(
                        new Error(key + " is a read-only property and can not be updated via this method.")
                    );
                }

                recordingTransformers[key] = knownMetadataProperty.recordingTransformer;
            }
        }

        return this.updateMetadataSet(id, kvps, recordingTransformers);
    }

    public addRecording(id: string, existingRecordingId?: string): Promise<string> {
        const recordingIdPromise = existingRecordingId
            ? Promise.resolve(existingRecordingId)
            : this.storageService.create(StorageTypes.recording);

        return recordingIdPromise.then((recordingId) =>
            this.storageService.load(id).then((container) => {
                const recordingGroupMetadata = <RecordingGroupMetadata>container.metadata;

                // Add recording to group
                recordingGroupMetadata.recordings.push(recordingId);
                const idx = recordingGroupMetadata.recordings.findIndex((x) => x === recordingId);

                // Instantiate default metadata values of recording on disk if it was a new recording
                const recordingInstantiationPromises = existingRecordingId
                    ? [Promise.resolve()]
                    : this.knownMetadataProperties
                          .filter((x) => !!x.recordingTransformer)
                          .map((x) => {
                              let value = (<unknown>recordingGroupMetadata)[x.key];
                              if (!value) value = x.defaultValue;

                              const transformedProps = x.recordingTransformer(
                                  x.key,
                                  value,
                                  recordingId,
                                  idx,
                                  recordingGroupMetadata
                              );

                              const storageServicePromises: Promise<void>[] = [];
                              for (const propKey in transformedProps) {
                                  if (Object.prototype.hasOwnProperty.call(transformedProps, propKey))
                                      storageServicePromises.push(
                                          this.storageService.updateMetadata(
                                              recordingId,
                                              propKey,
                                              transformedProps[propKey]
                                          )
                                      );
                              }

                              return Promise.all(storageServicePromises).then(() => {});
                          });

                // Update recordings of recordings group on disk
                return Promise.all(recordingInstantiationPromises)
                    .then(() => this.storageService.updateMetadata(id, "recordings", recordingGroupMetadata.recordings))
                    .then(() => recordingId);
            })
        );
    }

    public deleteRecording(id: string, recordingId: string): Promise<void> {
        return StorageServiceExtensions.removeRecordingFromGroup(this.storageService, recordingId, id).then(() =>
            this.storageService.delete(recordingId)
        );
    }

    //endregion

    //#region Helper Methods
    private getRecordingGroupContainerFromId(id: string): Promise<RecordingGroupContainer> {
        return this.storageService.load(id).then((x) => <RecordingGroupContainer>x);
    }
    //#endregion
}
